/* eslint-disable react-hooks/rules-of-hooks */
import { Check as CheckIcon } from "@mui/icons-material";
import { Box, BoxProps, CircularProgress, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { setNestedObjectValues, useFormikContext } from "formik";
import React from "react";
import { useLatest, useUpdateEffect } from "react-use";
import { assertNever } from "../../utils.js";
import { defaultIsEqualForm } from "../hooks/useAutoSave.js";

interface Props<V extends Record<string, any>> {
  /**
   * Function to filter current formik values before comparing with initial ones
   */
  filterValues?: (values: V) => V;
  /**
   * Initial values for enabling reinitialization
   */
  initialValues?: V;
  /**
   * Whether reinitialization is enabled
   */
  enableReinitialize?: boolean;
  /**
   * Whether auto-save feature is disabled
   */
  isAutoSaveDisabled?: boolean;
  /**
   * Debounce milliseconds after value change before actual save
   */
  debounceMs?: number;
  /**
   * Reset timeout in milliseconds before hiding "Changes saved" hint
   */
  resetMs?: number;
  /**
   * Whether to show text indicator, or icon indicator, or nothing
   */
  type?: "text" | "icon" | null;
  /**
   * Typography variant for indicator text
   */
  variant?: React.ComponentProps<typeof Typography>["variant"];
  /**
   * Justify style for indicator text
   */
  justifyContent?: React.CSSProperties["justifyContent"];
  /**
   * Custom equality check function
   */
  isEqualForm?: (v1: V | undefined, v2: V | undefined) => boolean;
  boxProps?: BoxProps;
}

export interface AutoSaveRef {
  /**
   * Force trigger saving process from outside
   */
  save: () => Promise<void>;
}

/**
 * Include this in a formik form to enable automatic submit on values change
 */
const _AutoSave = <V extends Record<string, any>>(
  {
    enableReinitialize = false,
    isAutoSaveDisabled = false,
    debounceMs = 650,
    resetMs = 3000,
    initialValues,
    filterValues,
    type = "text",
    variant = "body2",
    justifyContent = "flex-end",
    isEqualForm = defaultIsEqualForm,
    boxProps,
  }: Props<V>,
  forwardedRef: React.Ref<AutoSaveRef>
) => {
  const formik = useFormikContext<V>();
  const { t } = useTranslate("Global");

  // stores values that form has sent to the server
  const latestSubmittedValues = React.useRef<V | null>(null);
  // save values to detect actual change in initial values
  const latestInitialValues = React.useRef<V | undefined>(initialValues);
  // saved values to check actual values change
  const latestSavedValues = React.useRef<V>(formik.values);

  const options = React.useRef({
    resetMs,
    debounceMs,
    isAutoSaveDisabled,
    filterValues,
    isEqualForm,
  });
  options.current = {
    resetMs,
    debounceMs,
    isAutoSaveDisabled,
    filterValues,
    isEqualForm,
  };

  const formikRef = React.useRef(formik);
  formikRef.current = formik;

  const timeoutDebounceId = React.useRef<ReturnType<typeof setTimeout>>();
  const hasChanged = React.useRef<boolean>(false);

  React.useEffect(() => {
    if (enableReinitialize && !initialValues)
      console.error(
        "<AutoSave>",
        "When `enableReinitialize` set to true, `initialValues` should be also provided"
      );
  }, [enableReinitialize, initialValues]);

  React.useEffect(() => {
    if (formik.enableReinitialize && !isAutoSaveDisabled)
      console.error(
        "<AutoSave>",
        "`enableReinitialize` in Formik shouldn't be set to true when using AutoSave"
      );
  }, [formik.enableReinitialize, isAutoSaveDisabled]);

  // indicator that data was saved
  const [isSaved, setIsSaved] = React.useState(false);
  useUpdateEffect(() => {
    if (options.current.isAutoSaveDisabled) return;
    if (!formik.isSubmitting) {
      setIsSaved(true);

      const timeoutResetId = setTimeout(() => {
        setIsSaved(false);
      }, options.current.resetMs);

      return () => {
        setIsSaved(false);
        clearTimeout(timeoutResetId);
      };
    }
  }, [formik.isSubmitting]);

  // enable reinitialize feature
  useUpdateEffect(() => {
    if (
      !enableReinitialize ||
      options.current.isAutoSaveDisabled ||
      !initialValues
    )
      return;

    if (
      !options.current.isEqualForm(latestInitialValues.current, initialValues)
    ) {
      // initial values were actually changed!

      // 1. just save them in latest reference
      latestInitialValues.current = initialValues;

      // 2. trying to define whether we had pre-saved values and whether they are different or not
      if (
        !latestSubmittedValues.current ||
        !options.current.isEqualForm(
          latestSubmittedValues.current,
          initialValues
        )
      ) {
        // values have changed! need to do full reset here
        formikRef.current.setValues(initialValues, false);
        // they come from the server, so there is nothing to save back
        latestSavedValues.current = initialValues;
      }

      // 3. reset saved values to null, we don't need it anymore
      latestSubmittedValues.current = null;
    }
  }, [initialValues, enableReinitialize]);

  const autoSubmit = useLatest(async () => {
    if (formikRef.current.isSubmitting) {
      // an earlier submit is still in flight: retry once it has finished
      hasChanged.current = true;
      return;
    }

    // Validate the current values here rather than reading `isValid`: that is
    // the result of the last finished validation, and Formik validates
    // asynchronously, so for this change it can still be pending (e.g. a
    // validation that outlasts the debounce) and would drop a valid change.
    const errors = await formikRef.current.validateForm();
    if (Object.keys(errors).length > 0) {
      formikRef.current.setTouched(setNestedObjectValues(errors, true));
      return;
    }

    if (formikRef.current.isSubmitting) {
      // a submit started while this validation ran: retry once it has finished
      hasChanged.current = true;
      return;
    }

    // 1. we need to define current values using `filterValues` property
    const currentValues =
      options.current.filterValues?.(formikRef.current.values) ??
      formikRef.current.values;

    // 2. store last sent value in saved
    latestSubmittedValues.current = currentValues;
    // 3. actually submitting of the form
    await formikRef.current.submitForm();
  });

  // reacting on values change
  React.useEffect(() => {
    if (options.current.isAutoSaveDisabled) return;

    // need to wait till form is getting validated
    setTimeout(() => {
      // 1. we need to define current values using `filterValues` property
      const currentValues =
        options.current.filterValues?.(formikRef.current.values) ??
        formikRef.current.values;
      // 2. defining last saved values
      const savedValues =
        options.current.filterValues?.(latestSavedValues.current) ??
        latestSavedValues.current;

      // 3. if they are the save - just return
      if (options.current.isEqualForm(savedValues, currentValues)) {
        return;
      }

      const isValid = formikRef.current.isValid;
      const isSubmitting = formikRef.current.isSubmitting;

      // 4. save new last values
      if (isValid) {
        latestSavedValues.current = formikRef.current.values;
      }

      // 5. if form is still submitting - save in a flag
      if (isSubmitting) {
        // Just save a flag. Regardless of `isValid`, which may predate the
        // validation of this change: the retry after the submit goes through
        // `autoSubmit`, which validates the values current by then.
        hasChanged.current = true;
        return;
      }

      // 5. trigger debounced update
      if (timeoutDebounceId.current) clearTimeout(timeoutDebounceId.current);
      // `isValid` above may predate the validation of this change (Formik
      // validates asynchronously, and its re-render can land after this
      // timeout), so a change that makes an invalid form valid would be
      // dropped. `autoSubmit` validates the current values itself, and marks
      // the errors as touched when they are invalid.
      timeoutDebounceId.current = setTimeout(() => {
        autoSubmit.current();
      }, options.current.debounceMs);
    });
  }, [formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  // clean save timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutDebounceId.current) clearTimeout(timeoutDebounceId.current);
    };
  }, []);

  // submitting form again if values were change during submitting
  useUpdateEffect(() => {
    if (options.current.isAutoSaveDisabled) return;
    if (!formik.isSubmitting) {
      if (hasChanged.current) {
        hasChanged.current = false;

        // trigger debounced update
        if (timeoutDebounceId.current) clearTimeout(timeoutDebounceId.current);
        timeoutDebounceId.current = setTimeout(() => {
          autoSubmit.current();
        }, options.current.debounceMs);
      }
    }
  }, [formik.isSubmitting]);

  React.useImperativeHandle(forwardedRef, () => ({
    save: async () => {
      if (options.current.isAutoSaveDisabled) return;
      if (formikRef.current.isSubmitting) {
        hasChanged.current = true;
        return;
      }

      // trigger form submit if needed
      if (timeoutDebounceId.current) clearTimeout(timeoutDebounceId.current);
      await autoSubmit.current();
    },
  }));

  return type === "text" ? (
    formik.isSubmitting || isSaved ? (
      <Box
        {...boxProps}
        sx={[
          {
            display: "flex",
            justifyContent: justifyContent,
          },
          ...(Array.isArray(boxProps?.sx) ? boxProps?.sx : [boxProps?.sx]),
        ]}
      >
        <Typography variant={variant}>
          {formik.isSubmitting
            ? t("Saving...")
            : isSaved
              ? t("Saved changes")
              : null}
        </Typography>
      </Box>
    ) : null
  ) : type === "icon" ? (
    <Box
      {...boxProps}
      sx={[
        {
          width: "24px",
          height: "24px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
        ...(Array.isArray(boxProps?.sx) ? boxProps?.sx : [boxProps?.sx]),
      ]}
    >
      {formik.isSubmitting ? (
        <CircularProgress size={20} />
      ) : isSaved ? (
        <CheckIcon fontSize="small" />
      ) : null}
    </Box>
  ) : type === null ? null : (
    assertNever(type)
  );
};

export const AutoSave = React.forwardRef(_AutoSave);
